/**
 * Responsibility: whether the fleet is actually reporting.
 *
 * PLAN.md section 6.9 calls this most of the value of the GUI: it is how you
 * know the system is healthy *before* a drop. The top real-world failure is a
 * monitor that stopped days ago with nobody noticing, so a stale node is
 * styled as an error rather than as a neutral fact.
 *
 * **It is a Settings tab rather than its own page** because it is operator
 * business - it names the machines and what each is allowed to do - and
 * because nothing here is read daily. It sat in the main navigation beside
 * Watchlist and Live, which are read constantly, and that put a page nobody
 * opens where a page everybody opens should be.
 */
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { MetricCard, SectionCard } from '@kit/components';
import { useNodes } from '../../api/hooks/useNodes';
import { useHealth } from '../../api/hooks/useHealth';

function age(seconds: number | null): string {
  if (seconds === null) return 'never';
  if (seconds < 60) return `${Math.round(seconds)}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  return `${Math.round(seconds / 3600)}h ago`;
}

export function NodesCard() {
  const { data: nodes, error } = useNodes();
  const { data: health } = useHealth();

  const list = nodes ?? [];
  const stale = list.filter((node) => node.stale && !node.revoked);

  return (
    // A flex item defaults to min-width:auto, so anything with a large
    // intrinsic width would widen this column and slide the page sideways.
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      {error ? <Alert severity="error">{(error as Error).message}</Alert> : null}

      {stale.length > 0 ? (
        <Alert severity="error">
          {stale.length} node{stale.length > 1 ? 's are' : ' is'} not reporting:{' '}
          {stale.map((node) => node.node_id).join(', ')}. A node that never started and
          one that died silently look the same from here, and both mean it is checking
          nothing.
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        }}
      >
        <MetricCard label="Nodes" value={String(list.filter((node) => !node.revoked).length)} />
        <MetricCard
          label="Reporting"
          value={String(list.filter((node) => !node.stale && !node.revoked).length)}
          helper={stale.length > 0 ? `${stale.length} not reporting` : 'all reporting'}
        />
        <MetricCard
          label="Database"
          value={health?.database_reachable ? 'reachable' : 'unreachable'}
        />
        <MetricCard
          label="Channels"
          value={String(health?.notification_channels ?? 0)}
          helper={
            (health?.notification_channels ?? 0) > 0
              ? 'ntfy and email'
              : 'none configured - alerts go nowhere'
          }
        />
      </Box>

      <SectionCard title="Fleet">
        {list.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No nodes registered yet.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {list.map((node) => (
              <Box
                key={node.node_id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: node.revoked ? 'divider' : node.stale ? 'error.main' : 'divider',
                  opacity: node.revoked ? 0.55 : 1,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {node.node_id}
                  </Typography>
                  <Chip size="small" variant="outlined" label={node.role} />
                  {node.capabilities.map((capability) => (
                    <Chip
                      key={capability}
                      size="small"
                      variant="outlined"
                      color="secondary"
                      label={capability}
                    />
                  ))}
                  {node.revoked ? <Chip size="small" label="revoked" /> : null}
                  {node.degraded ? (
                    <Chip size="small" color="warning" variant="outlined" label="degraded" />
                  ) : null}
                  <Chip
                    size="small"
                    variant="outlined"
                    color={node.stale && !node.revoked ? 'error' : 'success'}
                    label={age(node.heartbeat_age_seconds)}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {node.watches_loaded} watches loaded
                  {node.queued_events > 0
                    ? ` - ${node.queued_events} events queued locally`
                    : ''}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </SectionCard>
    </Stack>
  );
}
